import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useManualFetch, type ManualFetchResult } from '@/lib/admin/hooks/useManualFetch';

// ─── Response types ────────────────────────────────────────────────────────────

export interface FunctionStats {
  slug: string;
  total_calls: number;
  success_calls: number;
  error_calls: number;
  avg_execution_ms: number | null;
  daily: Array<{ date: string; calls: number }>;
}

export interface AdminStatsResponse {
  edge_functions: {
    functions: FunctionStats[];
    period_days: number;
  };
  usage: {
    db_size_bytes: number | null;
    db_size_limit_bytes: number | null;
    storage_size_bytes: number | null;
    storage_size_limit_bytes: number | null;
    bandwidth_bytes: number | null;
    bandwidth_limit_bytes: number | null;
    monthly_active_users: number | null;
    mau_limit: number | null;
    func_invocations_month: number | null;
    func_invocations_limit: number | null;
  };
  db: {
    size_bytes: number;
    total_users: number;
    mau_30d: number;
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseAdminStatsOptions {
  /** Number of past days to include (default: 7). */
  days?: number;
}

export type UseAdminStatsResult = ManualFetchResult<AdminStatsResponse>;

/**
 * Statistiche edge functions + quota piano.
 *
 * Come `useDatabaseSizeStats`, il fetch parte SOLO quando si chiama `load()`:
 * nessun caricamento automatico al mount.
 */
export function useAdminStats(options: UseAdminStatsOptions = {}): UseAdminStatsResult {
  const { days = 7 } = options;

  const fetcher = useCallback(async (): Promise<AdminStatsResponse> => {
    const { data, error } = await supabase.functions.invoke('admin-stats', {
      body: { days },
    });
    if (error) throw new Error(`admin-stats: ${error.message}`);
    return data as AdminStatsResponse;
  }, [days]);

  return useManualFetch(fetcher, 'useAdminStats');
}

export default useAdminStats;
